import React from 'react';
import 'twin.macro';
import {
  RestorableItemType,
  RestoreFn,
} from '@teetsh/app/src/containers/UserSettings/Trash/components/RestorableList';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { format } from '@teetsh/app/src/utils/time/format';

interface Props<ItemType> {
  item: ItemType;
  restore: RestoreFn;
  details?: (item: ItemType) => React.ReactElement;
}

export function Card<ItemType extends RestorableItemType>(
  props: Props<ItemType>,
) {
  const { item, restore } = props;
  const { locale } = useUser();
  const { t } = useTranslation(['common', 'userSettings']);

  return (
    <div tw="pt-4 flex flex-col gap-2 text-sm">
      <div tw="flex justify-between items-start gap-4">
        <div>
          <div tw="mb-1 text-xs text-muted">
            {t('userSettings:restore.list.element')}
          </div>
          <div tw="font-semibold">{item.name}</div>
        </div>
        <Button
          minimal
          icon={ArrowUturnLeftIcon}
          onClick={() =>
            restore({
              id: item.id,
              ...('schoolyearId' in item && {
                schoolyearId: item.schoolyearId,
              }),
              ...('schoolId' in item && {
                schoolId: item.schoolId,
              }),
              ...('sequenceId' in item && {
                sequenceId: item.sequenceId,
              }),
              ...('type' in item && {
                type: item.type,
              }),
            })
          }
        />
      </div>

      {props.details?.(item)}

      {'schoolyear' in item && (
        <div>
          <div tw="mb-1 text-xs text-muted">
            {t(
              'schoolName' in item
                ? 'userSettings:restore.list.school'
                : 'userSettings:restore.list.schoolyear',
            )}
          </div>
          <div tw="font-semibold">
            {item.schoolyear - 1} - {item.schoolyear}
          </div>
          {'schoolName' in item && <div>{item.schoolName}</div>}
        </div>
      )}

      <div tw="flex flex-wrap gap-x-4 justify-between">
        <div>
          <div tw="mb-1 text-xs text-muted">
            {t('userSettings:restore.list.createdAt')}
          </div>
          <div>
            {t('common:day_at_time', {
              day: format(
                item.createdAt,
                locale,
                t('common:datePattern.dd/MM/yyyy'),
              ),
              separator: ' ',
              time: format(
                item.createdAt,
                locale,
                t('common:time.pattern_HH_mm'),
              ),
            })}
          </div>
        </div>

        <div>
          <div tw="mb-1 text-xs text-muted">
            {t('userSettings:restore.list.deletedAt')}
          </div>
          <div>
            {t('common:day_at_time', {
              day: format(
                item.deletedAt,
                locale,
                t('common:datePattern.dd/MM/yyyy'),
              ),
              separator: ' ',
              time: format(
                item.deletedAt,
                locale,
                t('common:time.pattern_HH_mm'),
              ),
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
